/* eslint-disable */
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import { useImmer } from "use-immer";
import AuthContext from "../../contexts/AuthProvider";

import "moment/locale/es";
import "moment/locale/pt";
import cx from "classnames";
import { client } from "../../services/axiosClient";
import { useTranslation } from "react-i18next";
import { calculateOomFromPlValues, getOom } from "../../utils/utils";
import { useScreenshot, createFileName } from "use-react-screenshot";
import ReactSelect from "react-select";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ChartDataLabels,
);

// Disable datalabels globally (will be enabled per chart)
ChartJS.defaults.set("plugins.datalabels", {
  display: false,
});

import HelpDialog from "../../components/Dialog/HelpDialog";
import {
  blackColor,
  grayColor,
  whiteColor,
  blendColors,
} from "../../assets/jss/material-dashboard-pro-react";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Hidden from "@material-ui/core/Hidden";
import Tooltip from "@material-ui/core/Tooltip";
import { ThreeDots } from "react-loader-spinner";

// @material-ui/icons
import {
  AttachMoney,
  HelpOutline,
  PhotoCamera,
  TrendingDown,
  TrendingUp,
} from "@material-ui/icons";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import ActionBar from "components/ActionBar/ActionBar";
import ActionDropdown from "../../components/ActionDropdown/ActionDropdown";
import useThemeColors from "../../hooks/useThemeColors";
import { useResultsFilters } from "../../hooks/useResultsFilters";
import ResultsFilterBar from "../../components/ResultsFilterBar/ResultsFilterBar";
import Pnl from "../../components/Pnl/Pnl";
import { PnlSharedProvider } from "../../hooks/usePnlShared";
import HelpTooltip from "components/HelpTooltip/HelpTooltip";

import styles from "../../assets/jss/material-dashboard-pro-react/views/trendsStyle";
import FullPageLoader from "../../components/Loaders/FullPageLoader";

// const NUM_GROUPS = 5;

export default function Trends() {
  const { userDetails } = useContext(AuthContext);
  const { clientColor } = useThemeColors();
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);

  // Comparison period state
  const [comparisonPeriod, setComparisonPeriod] = useState(null);

  // Filter states — gerenciados pelo useResultsFilters abaixo
  const filtersList = useRef([]);
  const filtersNames = useRef({});
  const filtersItems = useRef({});
  const filtersGroups = useRef({});

  const levelsCount = useRef(0);

  // P&L
  const volumeUnit = useRef("");
  const [volumeColumn, setVolumeColumn] = useState("volume");
  const volumeUnitAlternatives = useRef([]);
  const plStructure = useRef([]);
  const grossRevenueAnchor = useRef("");
  const [revenueReference, setRevenueReference] = useState(null);
  const revenueReferenceOptions = useRef([]);
  const netRevenueAnchor = useRef("");
  const serveMarginAnchor = useRef("");
  const revenueAnchors = useRef([]);
  const marginAnchors = useRef([]);
  const volumeL12M = useRef(null);
  const [plRefreshing, setPlRefreshing] = useState(false);
  const [plValues, setPlValues] = useImmer({});
  const [plNames, setPlNames] = useState({ 1: "", 2: "" });
  const [driversData, setDriversData] = useState(null);
  const [driversRefreshing, setDriversRefreshing] = useState(false);

  const [oom, setOom] = useState({
    div: 1e6,
    x: " " + t("oom.m"),
  });
  const [volumeOom, setVolumeOom] = useState({
    div: 1,
    x: "",
  });

  // other states
  const [currencyUnit, setCurrencyUnit] = useState("BRL");
  const currencyUnitOptions = useRef([]);
  const currencyUnitOptionsAvailable = useRef([]);
  const [colorMetric, setColorMetric] = useState("serve_margin");
  const [drillDownsOpen, setDrillDownsOpen] = useState([]);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  // Mouse over states for P&L highlighting
  const [mouseOverPLLine, setMouseOverPLLine] = useState(null);
  const [mouseOverPLLineFade, setMouseOverPLLineFade] = useState(null);
  const timersAllowed = useRef(false);

  // Screenshot functionality
  const trendsRef = useRef(null);
  const [, takeScreenShot] = useScreenshot({
    type: "image/png",
    quality: 1.0,
  });
  const download = (
    image,
    { name = "graph_explorer_screenshot", extension = "png" } = {},
  ) => {
    const a = document.createElement("a");
    a.href = image;
    a.download = createFileName(extension, name);
    a.click();
  };
  const downloadScreenshot = () =>
    takeScreenShot(trendsRef.current).then(download);

  // ── useResultsFilters ─────────────────────────────────────────────────────
  // Callback ref para passar P&L config extraído do meta para o estado local
  const trendsMetaCallback = useRef(null);

  const {
    filtersList: _filtersList,
    filtersNames: _filtersNames,
    filtersItems: _filtersItems,
    filtersGroups: _filtersGroups,
    commonFilters,
    setCommonFilters,
    commonFiltersIndexes,
    setCommonFiltersIndexes,
    commonFiltersMode,
    setCommonFiltersMode,
    commonFiltersSequence,
    setCommonFiltersSequence,
    rangeFilters,
    clearRangeFilter,
    handleResetFilters,
    removeFilter,
    filterMenuClickHandler,
    persist,
    setPersist,
    dimFilterDialogProps,
    dimSections,
  } = useResultsFilters({
    metaEndpoint: "api/results_trends_meta/",
    includePeriod: false,
    getDimInfoExtra: () => ({
      hasRefRange: true,
      plLines: plStructure.current.map((l) => ({
        key: l.meta_name,
        label: l.name,
        level: l.level,
        isParent: l.is_parent,
      })),
      serveMarginAnchor: serveMarginAnchor.current,
      revenueAnchors: revenueAnchors.current,
    }),
    onMetaLoaded: (response) => trendsMetaCallback.current?.(response),
  });

  // Sincroniza refs de metadados do hook para o escopo local
  filtersList.current = _filtersList.current;
  filtersNames.current = _filtersNames.current;
  filtersItems.current = _filtersItems.current;
  filtersGroups.current = _filtersGroups.current;

  // Helper functions for driver rendering
  const formatPrice = (value) => {
    if (value >= 1000) {
      return Math.round(value).toLocaleString();
    }
    return value.toFixed(2);
  };

  const formatChangeValue = useCallback(
    (driver) => {
      const {
        effect_type,
        avg_price_p1,
        avg_price_p2,
        margin_pct_p1,
        margin_pct_p2,
        avg_revenue_share_p1,
        avg_revenue_share_p2,
        volume_share_p1,
        volume_share_p2,
        composition_effect,
        cost_pct_p1,
        cost_pct_p2,
        cost_line_pct_p1,
        cost_line_pct_p2,
      } = driver;

      if (effect_type === "price") {
        const currency = currencyUnit || "ARS";
        const volUnit = volumeUnit.current || "hL";
        return `${formatPrice(avg_price_p1)} › ${formatPrice(avg_price_p2)} ${currency}/${volUnit}`;
      } else if (effect_type === "mix") {
        // Use VOLUME share for mix effect (consistent with mix_effect calculation)
        const share1 = (volume_share_p1 || 0) * 100;
        const share2 = (volume_share_p2 || 0) * 100;
        const isDecrease = share2 < share1;
        const relevantMargin = isDecrease ? margin_pct_p1 : margin_pct_p2;
        const marginLabel = isDecrease
          ? t("results.trends.margin_previous")
          : t("results.trends.margin_recent");
        return `${t("results.trends.share")}: ${share1.toFixed(1)}% › ${share2.toFixed(1)}%, ${marginLabel}: ${relevantMargin.toFixed(1)}%`;
      } else if (effect_type === "cost") {
        const cost1 =
          cost_line_pct_p1 !== undefined && cost_line_pct_p1 !== null
            ? cost_line_pct_p1
            : cost_pct_p1 !== undefined
              ? cost_pct_p1
              : 100 - (margin_pct_p1 || 0);
        const cost2 =
          cost_line_pct_p2 !== undefined && cost_line_pct_p2 !== null
            ? cost_line_pct_p2
            : cost_pct_p2 !== undefined
              ? cost_pct_p2
              : 100 - (margin_pct_p2 || 0);
        return `${cost1.toFixed(1)}% › ${cost2.toFixed(1)}%`;
      } else if (effect_type === "composition" || composition_effect) {
        const isNew = (avg_revenue_share_p2 || 0) > (avg_revenue_share_p1 || 0);
        const share =
          (isNew ? avg_revenue_share_p2 : avg_revenue_share_p1) * 100;
        const margin = isNew ? margin_pct_p2 : margin_pct_p1;
        return `${t("results.trends.share")}: ${share.toFixed(1)}%, ${t("results.trends.margin")}: ${margin.toFixed(1)}%`;
      }

      return "";
    },
    [currencyUnit, volumeUnit, t],
  );

  const buildNarrative = useCallback(
    (driver) => {
      const {
        element,
        effect_type,
        dimension,
        dimension_label,
        avg_price_p1,
        avg_price_p2,
        margin_pct_p1,
        margin_pct_p2,
        cost_pct_p1,
        cost_pct_p2,
        avg_revenue_share_p1,
        avg_revenue_share_p2,
        volume_share_p1,
        volume_share_p2,
        cost_line_pct_p1,
        cost_line_pct_p2,
        dominant_cost_line_name,
        total_impact,
      } = driver;

      // Get dimension name for display
      let dimName = "";
      if (dimension_label) {
        dimName =
          typeof dimension_label === "string"
            ? dimension_label
            : dimension_label[i18n.language] || dimension_label.en || "";
      }
      if (!dimName) {
        const dimKey = typeof dimension === "string" ? dimension : dimension.en;
        const filterName = filtersNames.current[dimKey];
        dimName = filterName
          ? typeof filterName === "string"
            ? filterName
            : filterName[i18n.language] || filterName.en
          : dimKey;
      }

      const elementDisplay =
        element === "" || element === null || element === undefined
          ? t("results.blank")
          : element;

      // Simplified narratives: "[Dimension] ELEMENT → CAUSE (X% › Y%)"

      const formatPercent = (val) => {
        if (val === null || val === undefined) return "—";
        return `${val.toFixed(1)}%`;
      };

      const formatCurrency = (val) => {
        if (!val) return "—";
        return new Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency: currencyUnit || "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(val);
      };

      const calcPercentChange = (p1, p2) => {
        if (!p1 || p1 === 0) return 0;
        return ((p2 - p1) / p1) * 100;
      };

      let beforeElement = `${dimName} `;
      let afterElement = "";

      if (effect_type === "price") {
        const priceChange = calcPercentChange(avg_price_p1, avg_price_p2);
        const priceIncreased = priceChange > 0;
        const priceDisplay = `(${formatCurrency(avg_price_p1)} › ${formatCurrency(avg_price_p2)})`;

        const causeText = priceIncreased
          ? t("results.trends.price_increase")
          : t("results.trends.price_decrease");
        afterElement = ` → <strong>${causeText}</strong> ${t("results.trends.of")} <strong>${formatPercent(Math.abs(priceChange))}</strong> ${priceDisplay}`;
      } else if (effect_type === "cost") {
        // Determine cost change direction based on the DOMINANT cost line if available,
        // otherwise use total cost. This ensures narrative is consistent with displayed values.
        let costIncreased;
        let costLineDetail = "";

        if (
          dominant_cost_line_name &&
          cost_line_pct_p1 !== null &&
          cost_line_pct_p2 !== null
        ) {
          // Use dominant cost line direction (consistent with displayed values)
          // Note: values can be negative (credits/reversals in accounting)
          // costIncreased = true when P2 > P1 (cost went up or credit went down)
          costIncreased = cost_line_pct_p2 > cost_line_pct_p1;
          // Display percentages with their signs (negative = credit)
          const pct1Display =
            cost_line_pct_p1 < 0
              ? `-${formatPercent(Math.abs(cost_line_pct_p1))}`
              : formatPercent(cost_line_pct_p1);
          const pct2Display =
            cost_line_pct_p2 < 0
              ? `-${formatPercent(Math.abs(cost_line_pct_p2))}`
              : formatPercent(cost_line_pct_p2);
          costLineDetail = ` ${t("results.trends.in")} '${dominant_cost_line_name}' (${pct1Display} › ${pct2Display})`;
        } else {
          // Fallback to total cost direction
          const costChange = calcPercentChange(cost_pct_p1, cost_pct_p2);
          costIncreased = costChange > 0;
        }

        const causeText = costIncreased
          ? t("results.trends.cost_increase")
          : t("results.trends.cost_decrease");
        afterElement = ` → <strong>${causeText}</strong>${costLineDetail}`;
      } else if (effect_type === "mix") {
        // Mix effect: change in VOLUME participation with above/below average margin
        // IMPORTANT: Use VOLUME share (not revenue share) to be consistent with mix_effect calculation
        // mix_effect = (volume_p2 - volume_p1) × unit_margin_p1
        const volShareP1Pct = (volume_share_p1 || 0) * 100;
        const volShareP2Pct = (volume_share_p2 || 0) * 100;
        const participationIncreased = volShareP2Pct > volShareP1Pct;

        // FIXED: Compare element margin directly with global average margin
        // instead of inferring from impact direction (which could be wrong due to other effects)
        // Use P2 margin comparison as that's the "current" state
        const globalMarginP2 = driversData?.global_margin_pct_p2 || 0;
        const marginAboveAverage = margin_pct_p2 > globalMarginP2;

        const causeText = participationIncreased
          ? t("results.trends.participation_increase")
          : t("results.trends.participation_decrease");

        const marginContext = marginAboveAverage
          ? t("results.trends.margin_above_average")
          : t("results.trends.margin_below_average");

        const participationDisplay = `(${formatPercent(volShareP1Pct)} › ${formatPercent(volShareP2Pct)})`;

        afterElement = ` → <strong>${causeText}</strong> ${participationDisplay} ${marginContext}`;
      } else {
        // Fallback
        afterElement = ` → <strong>Δ ${t("results.trends.margin_short")}</strong> (${formatPercent(margin_pct_p1)} › ${formatPercent(margin_pct_p2)})`;
      }

      return { beforeElement, element: elementDisplay, afterElement };
    },
    [i18n.language, filtersNames, t, currencyUnit, driversData],
  );

  // Calculate max impact from current drivers data for relative arrow positioning
  const maxDriverImpact = useMemo(() => {
    if (!driversData) return 1;

    const allImpacts = [
      ...(driversData.main_losses || []).map((d) => Math.abs(d.total_impact)),
      ...(driversData.main_gains || []).map((d) => Math.abs(d.total_impact)),
    ];

    if (allImpacts.length === 0) return 1;
    return Math.max(...allImpacts);
  }, [driversData]);

  const getRelevanceArrow = useCallback(
    (totalImpact) => {
      const absImpact = Math.abs(totalImpact);
      const isNegative = totalImpact < 0;

      let angle = 0;
      let color = "";
      let relevanceLevel = "";

      // Relative thresholds based on current data
      const highThreshold = maxDriverImpact * 0.66; // 66% of max
      const mediumThreshold = maxDriverImpact * 0.33; // 33% of max

      if (absImpact >= highThreshold) {
        angle = isNegative ? -90 : 90;
        color = isNegative ? "#c62828" : "#2e7d32";
        relevanceLevel = "high";
      } else if (absImpact >= mediumThreshold) {
        angle = isNegative ? -60 : 60;
        color = isNegative ? "#f57c00" : "#7cb342";
        relevanceLevel = "medium";
      } else {
        angle = isNegative ? -30 : 30;
        color = isNegative ? "#757575" : "#9e9e9e";
        relevanceLevel = "low";
      }

      angle = -angle;

      return { angle, color, relevanceLevel };
    },
    [maxDriverImpact],
  );

  const getInconsistencyTag = useCallback(() => {
    return {
      label: t("results.trends.inconsistency"),
      color: "#d84315",
      bgColor: "#ffe0b2",
    };
  }, [t]);

  const getUnitOptions = () => {
    let options = [];
    // currencies
    options.push({
      explainer: true,
      text: t("results.unit_blocks.currency"),
    });
    currencyUnitOptions.current.map((opt) => {
      let available = currencyUnitOptionsAvailable.current.includes(opt);
      options.push({
        identified: {
          option_type: "currency",
          value: opt,
        },
        text:
          opt +
          (available
            ? ""
            : " (" +
              t("results.missing_rates") +
              (["DEV", "IMG", "CAD", "CFA", "CST"].includes(
                userDetails.user_role,
              )
                ? " - " + t("results.missing_rates_complement")
                : "") +
              ")"),
        active: opt === currencyUnit,
        disabled: !available,
      });
    });
    // revenue reference (only show if more than 1 non-combined option)
    const filteredRevRefOptions = revenueReferenceOptions.current.filter(
      (opt) => opt.value !== "combined",
    );
    if (filteredRevRefOptions.length > 1) {
      options.push({ divider: true });
      options.push({
        explainer: true,
        text: t("results.unit_blocks.revenue_reference"),
      });
      filteredRevRefOptions.forEach((opt) => {
        options.push({
          identified: {
            option_type: "revenue_reference",
            value: opt.value,
          },
          text: opt.label,
          active: revenueReference === opt.value,
        });
      });
    }
    // volume unit alternatives (only show if more than 1 option)
    if (volumeUnitAlternatives.current.length > 1) {
      options.push({ divider: true });
      options.push({
        explainer: true,
        text: t("results.unit_blocks.volume"),
      });
      volumeUnitAlternatives.current.forEach((opt) => {
        options.push({
          identified: {
            option_type: "volume",
            value: opt.column,
          },
          text: opt.label,
          active: volumeColumn === opt.column,
        });
      });
    }
    options.push({ divider: true });
    // color metric
    options.push({
      explainer: true,
      text: t("results.unit_blocks.color_metric"),
    });
    options.push({
      identified: {
        option_type: "color_metric",
        value: "serve_margin",
      },
      text: t("results.serve_margin"),
      active: colorMetric === "serve_margin",
    });
    options.push({
      identified: {
        option_type: "color_metric",
        value: "per_volume",
      },
      text: t("results.value") + " / " + volumeUnit.current,
      active: colorMetric === "per_volume",
    });
    return options;
  };
  const handleUnitSelection = (opt) => {
    const { option_type, value } = opt;
    if (option_type === "currency") {
      if (value !== currencyUnit) {
        setCurrencyUnit(value);
      }
    } else if (option_type === "color_metric") {
      setColorMetric(value);
    } else if (option_type === "revenue_reference") {
      setRevenueReference(value);
    } else if (option_type === "volume") {
      if (value !== volumeColumn) {
        setVolumeColumn(value);
        const selectedAlt = volumeUnitAlternatives.current.find(
          (o) => o.column === value,
        );
        if (selectedAlt) {
          volumeUnit.current = selectedAlt.label;
        }
      }
    }
  };

  const handleHelpClick = () => {
    setHelpDialogOpen(true);
  };

  // Mouse over handlers for P&L line highlighting
  const handleMouseOverPLLine = (meta_name) => {
    setMouseOverPLLine(meta_name);
    setMouseOverPLLineFade(meta_name);
  };

  const handleMouseLeavePLLine = () => {
    setMouseOverPLLine(null);
    setMouseOverPLLineFade(null);
  };

  const allowTimers = () => {
    timersAllowed.current = true;
  };

  const disallowTimers = () => {
    timersAllowed.current = false;
  };

  const calculatePlDeltas = useMemo(() => {
    if (
      !plValues[1] ||
      !plValues[2] ||
      !plStructure.current ||
      plStructure.current.length === 0
    ) {
      return null;
    }

    const revenue1 = plValues[1]?.[netRevenueAnchor.current];
    const revenue2 = plValues[2]?.[netRevenueAnchor.current];

    if (!revenue1 || !revenue2) {
      return null;
    }

    const deltas = {};
    plStructure.current.forEach((line) => {
      const metaName = line.meta_name;
      const value1 = plValues[1]?.[metaName];
      const value2 = plValues[2]?.[metaName];

      if (
        value1 !== undefined &&
        value1 !== null &&
        value2 !== undefined &&
        value2 !== null
      ) {
        const percent1 = (100 * value1) / revenue1;
        const percent2 = (100 * value2) / revenue2;
        deltas[metaName] = percent2 - percent1;
      }
    });

    return Object.keys(deltas).length > 0 ? deltas : null;
  }, [plValues, plStructure, netRevenueAnchor]);

  const changeIndicators = useMemo(() => {
    if (!plValues[1] || !plValues[2]) {
      return null;
    }

    const volume1 = plValues[1]?.volume;
    const volume2 = plValues[2]?.volume;
    const grossRevenue1 = plValues[1]?.[grossRevenueAnchor.current];
    const grossRevenue2 = plValues[2]?.[grossRevenueAnchor.current];
    const netRevenue1 = plValues[1]?.[netRevenueAnchor.current];
    const netRevenue2 = plValues[2]?.[netRevenueAnchor.current];
    const serveMargin1 = plValues[1]?.[serveMarginAnchor.current];
    const serveMargin2 = plValues[2]?.[serveMarginAnchor.current];

    if (
      !volume1 ||
      !volume2 ||
      !grossRevenue1 ||
      !grossRevenue2 ||
      !netRevenue1 ||
      !netRevenue2
    ) {
      return null;
    }

    const avgGrossPrice1 = grossRevenue1 / volume1;
    const avgGrossPrice2 = grossRevenue2 / volume2;
    const serveMarginPercent1 = netRevenue1
      ? (100 * serveMargin1) / netRevenue1
      : 0;
    const serveMarginPercent2 = netRevenue2
      ? (100 * serveMargin2) / netRevenue2
      : 0;

    return {
      volume: {
        delta: volume2 - volume1,
        deltaPercent: volume1 ? ((volume2 - volume1) / volume1) * 100 : 0,
      },
      grossPrice: {
        delta: avgGrossPrice2 - avgGrossPrice1,
        deltaPercent: avgGrossPrice1
          ? ((avgGrossPrice2 - avgGrossPrice1) / avgGrossPrice1) * 100
          : 0,
      },
      netRevenue: {
        delta: netRevenue2 - netRevenue1,
        deltaPercent: netRevenue1
          ? ((netRevenue2 - netRevenue1) / netRevenue1) * 100
          : 0,
      },
      serveMargin: {
        delta: serveMargin2 - serveMargin1,
        deltaPercentagePoints: serveMarginPercent2 - serveMarginPercent1,
      },
    };
  }, [plValues, grossRevenueAnchor, netRevenueAnchor, serveMarginAnchor]);

  // Waterfall analysis calculation using Numerator/Denominator decomposition
  const waterfallData = useMemo(() => {
    if (!plValues[1] || !plValues[2]) {
      return null;
    }

    const volume1 = plValues[1]?.volume;
    const volume2 = plValues[2]?.volume;
    const grossRevenue1 = plValues[1]?.[grossRevenueAnchor.current];
    const grossRevenue2 = plValues[2]?.[grossRevenueAnchor.current];
    const netRevenue1 = plValues[1]?.[netRevenueAnchor.current];
    const netRevenue2 = plValues[2]?.[netRevenueAnchor.current];
    const serveMargin1 = plValues[1]?.[serveMarginAnchor.current];
    const serveMargin2 = plValues[2]?.[serveMarginAnchor.current];

    if (
      !volume1 ||
      !volume2 ||
      !grossRevenue1 ||
      !grossRevenue2 ||
      !netRevenue1 ||
      !netRevenue2 ||
      !serveMargin1 ||
      !serveMargin2
    ) {
      return null;
    }

    // Margin % for P1 and P2
    const marginPercent1 = (100 * serveMargin1) / netRevenue1;
    const marginPercent2 = (100 * serveMargin2) / netRevenue2;
    const totalDelta = marginPercent2 - marginPercent1;

    // Absolute variations
    const deltaSM = serveMargin2 - serveMargin1;
    const deltaNR = netRevenue2 - netRevenue1;
    const deltaVol = volume2 - volume1;

    // Unit values
    const marginUnit1 = serveMargin1 / volume1;
    const marginUnit2 = serveMargin2 / volume2;
    const revenueUnit1 = netRevenue1 / volume1;
    const revenueUnit2 = netRevenue2 / volume2;

    // Step 1: Exact margin% decomposition
    // Numerator Effect: margin% change if only SM changed (NR constant)
    const numeratorEffect = (deltaSM / netRevenue1) * 100;

    // Denominator Effect: margin% change if only NR changed (SM constant)
    const denominatorEffect =
      serveMargin1 * (1 / netRevenue2 - 1 / netRevenue1) * 100;

    // Interaction: cross effect (usually small)
    const interactionEffect =
      deltaSM * (1 / netRevenue2 - 1 / netRevenue1) * 100;

    // Step 2: Decompose Numerator Effect into Price/Cost/Mix
    // SM change due to volume (mix effect)
    const deltaSM_mix = deltaVol * marginUnit1;

    // SM change due to unit margin
    const deltaSM_unitMargin = volume2 * (marginUnit2 - marginUnit1);

    // Decompose unit margin change into price and cost components
    const grossPriceUnit1 = grossRevenue1 / volume1;
    const grossPriceUnit2 = grossRevenue2 / volume2;
    const costUnit1 = (grossRevenue1 - serveMargin1) / volume1;
    const costUnit2 = (grossRevenue2 - serveMargin2) / volume2;

    // Price contribution to SM change
    const deltaSM_price = volume2 * (grossPriceUnit2 - grossPriceUnit1);

    // Cost contribution to SM change
    const deltaSM_cost = volume2 * (costUnit1 - costUnit2);

    // Numerator contributions (normalized)
    const numPrice = (deltaSM_price / netRevenue1) * 100;
    const numCost = (deltaSM_cost / netRevenue1) * 100;
    const numMix = (deltaSM_mix / netRevenue1) * 100;

    // Step 3: Distribute denominatorEffect between Price and Mix
    // Revenue change contributions
    const deltaNR_volume = deltaVol * revenueUnit1;
    const deltaNR_price = volume2 * (revenueUnit2 - revenueUnit1);

    // Proportional distribution (using absolute values)
    const totalDeltaNR_abs = Math.abs(deltaNR_volume) + Math.abs(deltaNR_price);

    let denomToPrice = 0;
    let denomToMix = 0;

    if (totalDeltaNR_abs > 0.001) {
      denomToPrice =
        denominatorEffect * (Math.abs(deltaNR_price) / totalDeltaNR_abs);
      denomToMix =
        denominatorEffect * (Math.abs(deltaNR_volume) / totalDeltaNR_abs);
    } else {
      denomToPrice = denominatorEffect / 2;
      denomToMix = denominatorEffect / 2;
    }

    // Aggregate: numEffect + denominator portion
    const priceEffectRaw = numPrice + denomToPrice;
    const costEffectRaw = numCost;
    const mixEffectRaw = numMix + denomToMix;

    // Step 4: Distribute interaction proportionally

    const sumRaw = priceEffectRaw + costEffectRaw + mixEffectRaw;
    const totalAbs =
      Math.abs(priceEffectRaw) +
      Math.abs(costEffectRaw) +
      Math.abs(mixEffectRaw);

    let priceEffect, costEffect, mixEffect;

    if (totalAbs > 0) {
      priceEffect =
        priceEffectRaw +
        interactionEffect * (Math.abs(priceEffectRaw) / totalAbs);
      costEffect =
        costEffectRaw +
        interactionEffect * (Math.abs(costEffectRaw) / totalAbs);
      mixEffect =
        mixEffectRaw + interactionEffect * (Math.abs(mixEffectRaw) / totalAbs);
    } else {
      priceEffect = interactionEffect / 3;
      costEffect = interactionEffect / 3;
      mixEffect = interactionEffect / 3;
    }

    return {
      marginInitial: marginPercent1,
      marginFinal: marginPercent2,
      totalDelta: totalDelta,
      priceEffect: priceEffect,
      costEffect: costEffect,
      mixEffect: mixEffect,
    };
  }, [plValues, grossRevenueAnchor, netRevenueAnchor, serveMarginAnchor]);

  const [availablePeriods, setAvailablePeriods] = useState([]);
  const comparisonPeriodInitialized = useRef(false);

  const isPeriodAvailable = useCallback(
    (period) => {
      return availablePeriods.includes(period);
    },
    [availablePeriods],
  );

  const getPeriodsRange = useCallback((latestPeriod, numMonths, offset = 0) => {
    const periods = [];
    let year = Math.floor(latestPeriod / 100);
    let month = latestPeriod % 100;

    for (let i = 0; i < offset; i++) {
      month--;
      if (month === 0) {
        month = 12;
        year--;
      }
    }

    for (let i = 0; i < numMonths; i++) {
      periods.push(year * 100 + month);
      month--;
      if (month === 0) {
        month = 12;
        year--;
      }
    }

    return periods;
  }, []);

  const getYTDPeriods = useCallback((latestPeriod) => {
    const periods = [];
    const year = Math.floor(latestPeriod / 100);
    const lastMonth = latestPeriod % 100;

    for (let month = 1; month <= lastMonth; month++) {
      periods.push(year * 100 + month);
    }

    return periods;
  }, []);

  // Check if all periods in an array are available
  const areAllPeriodsAvailable = useCallback(
    (periods) => {
      return periods.every((period) => isPeriodAvailable(period));
    },
    [isPeriodAvailable],
  );

  // Determine which comparison options are available
  const getComparisonPeriodOptions = useCallback(() => {
    if (availablePeriods.length === 0) {
      return [
        {
          value: "ytd_vs_ytd",
          label: t("results.trends.ytd_vs_ytd"),
          isDisabled: true,
        },
        {
          value: "last_month_vs_ly",
          label: t("results.trends.last_month_vs_ly"),
          isDisabled: true,
        },
        {
          value: "last_3m_vs_ly",
          label: t("results.trends.last_3m_vs_ly"),
          isDisabled: true,
        },
        {
          value: "last_6m_vs_ly",
          label: t("results.trends.last_6m_vs_ly"),
          isDisabled: true,
        },
        {
          value: "last_month_vs_prev",
          label: t("results.trends.last_month_vs_prev"),
          isDisabled: true,
        },
        {
          value: "last_3m_vs_prev",
          label: t("results.trends.last_3m_vs_prev"),
          isDisabled: true,
        },
        {
          value: "last_6m_vs_prev",
          label: t("results.trends.last_6m_vs_prev"),
          isDisabled: true,
        },
        {
          value: "last_12m_vs_prev",
          label: t("results.trends.last_12m_vs_prev"),
          isDisabled: true,
        },
      ];
    }

    const latestPeriod = Math.max(...availablePeriods);
    const latestYear = Math.floor(latestPeriod / 100);
    const latestMonth = latestPeriod % 100;

    // YTD x YTD: current year YTD + same period last year
    const currentYTD = getYTDPeriods(latestPeriod);
    const lastYearYTD = getYTDPeriods((latestYear - 1) * 100 + latestMonth);
    const ytdAvailable = areAllPeriodsAvailable([
      ...currentYTD,
      ...lastYearYTD,
    ]);

    // Last month vs same period last year
    const lastMonth = [latestPeriod];
    const lastMonthLY = [(latestYear - 1) * 100 + latestMonth];
    const lastMonthVsLYAvailable = areAllPeriodsAvailable([
      ...lastMonth,
      ...lastMonthLY,
    ]);

    // Last 3 months vs same period last year
    const last3Months = getPeriodsRange(latestPeriod, 3);
    const last3MonthsLY = last3Months.map((p) => {
      const y = Math.floor(p / 100);
      const m = p % 100;
      return (y - 1) * 100 + m;
    });
    const last3MVsLYAvailable = areAllPeriodsAvailable([
      ...last3Months,
      ...last3MonthsLY,
    ]);

    // Last 6 months vs same period last year
    const last6Months = getPeriodsRange(latestPeriod, 6);
    const last6MonthsLY = last6Months.map((p) => {
      const y = Math.floor(p / 100);
      const m = p % 100;
      return (y - 1) * 100 + m;
    });
    const last6MVsLYAvailable = areAllPeriodsAvailable([
      ...last6Months,
      ...last6MonthsLY,
    ]);

    // Last month vs previous month
    const lastMonthVsPrev = getPeriodsRange(latestPeriod, 2);
    const lastMonthVsPrevAvailable = areAllPeriodsAvailable(lastMonthVsPrev);

    // Last 3 months vs previous 3 months
    const last3MVsPrev = getPeriodsRange(latestPeriod, 6);
    const last3MVsPrevAvailable = areAllPeriodsAvailable(last3MVsPrev);

    // Last 6 months vs previous 6 months
    const last6MVsPrev = getPeriodsRange(latestPeriod, 12);
    const last6MVsPrevAvailable = areAllPeriodsAvailable(last6MVsPrev);

    // Last 12 months vs previous 12 months
    const last12MVsPrev = getPeriodsRange(latestPeriod, 24);
    const last12MVsPrevAvailable = areAllPeriodsAvailable(last12MVsPrev);

    return [
      {
        value: "ytd_vs_ytd",
        label: t("results.trends.ytd_vs_ytd"),
        isDisabled: !ytdAvailable,
      },
      {
        value: "last_month_vs_ly",
        label: t("results.trends.last_month_vs_ly"),
        isDisabled: !lastMonthVsLYAvailable,
      },
      {
        value: "last_3m_vs_ly",
        label: t("results.trends.last_3m_vs_ly"),
        isDisabled: !last3MVsLYAvailable,
      },
      {
        value: "last_6m_vs_ly",
        label: t("results.trends.last_6m_vs_ly"),
        isDisabled: !last6MVsLYAvailable,
      },
      {
        value: "last_month_vs_prev",
        label: t("results.trends.last_month_vs_prev"),
        isDisabled: !lastMonthVsPrevAvailable,
      },
      {
        value: "last_3m_vs_prev",
        label: t("results.trends.last_3m_vs_prev"),
        isDisabled: !last3MVsPrevAvailable,
      },
      {
        value: "last_6m_vs_prev",
        label: t("results.trends.last_6m_vs_prev"),
        isDisabled: !last6MVsPrevAvailable,
      },
      {
        value: "last_12m_vs_prev",
        label: t("results.trends.last_12m_vs_prev"),
        isDisabled: !last12MVsPrevAvailable,
      },
    ];
  }, [
    getPeriodsRange,
    getYTDPeriods,
    areAllPeriodsAvailable,
    availablePeriods,
    t,
  ]);

  const comparisonPeriodOptions = useMemo(
    () => getComparisonPeriodOptions(),
    [getComparisonPeriodOptions, i18n.language],
  );

  // Auto-select the first available option when periods are loaded (only once)
  useEffect(() => {
    if (
      comparisonPeriodOptions.length > 0 &&
      !comparisonPeriodInitialized.current
    ) {
      const firstAvailable = comparisonPeriodOptions.find(
        (opt) => !opt.isDisabled,
      );
      if (firstAvailable) {
        setComparisonPeriod(firstAvailable.value);
        comparisonPeriodInitialized.current = true;
      }
    }
  }, [comparisonPeriodOptions]);

  const handleComparisonPeriodChange = useCallback((option) => {
    if (option && !option.isDisabled) {
      setComparisonPeriod(option.value);
    }
  }, []);

  // Function to get periods for each P&L based on comparison selection
  const getPeriodsForComparison = useCallback(
    (comparisonType) => {
      if (!comparisonType || availablePeriods.length === 0) return null;

      const latestPeriod = Math.max(...availablePeriods);
      const latestYear = Math.floor(latestPeriod / 100);
      const latestMonth = latestPeriod % 100;

      switch (comparisonType) {
        case "ytd_vs_ytd": {
          const currentYTD = getYTDPeriods(latestPeriod);
          const lastYearYTD = getYTDPeriods(
            (latestYear - 1) * 100 + latestMonth,
          );
          return {
            period1: lastYearYTD,
            period2: currentYTD,
          };
        }
        case "last_month_vs_ly": {
          const currentMonth = [latestPeriod];
          const lastYearMonth = [(latestYear - 1) * 100 + latestMonth];
          return {
            period1: lastYearMonth,
            period2: currentMonth,
          };
        }
        case "last_3m_vs_ly": {
          const currentPeriods = getPeriodsRange(latestPeriod, 3, 0);
          const lastYearPeriods = getPeriodsRange(
            (latestYear - 1) * 100 + latestMonth,
            3,
            0,
          );
          return {
            period1: lastYearPeriods,
            period2: currentPeriods,
          };
        }
        case "last_6m_vs_ly": {
          const currentPeriods = getPeriodsRange(latestPeriod, 6, 0);
          const lastYearPeriods = getPeriodsRange(
            (latestYear - 1) * 100 + latestMonth,
            6,
            0,
          );
          return {
            period1: lastYearPeriods,
            period2: currentPeriods,
          };
        }
        case "last_month_vs_prev": {
          const currentMonth = [latestPeriod];
          const prevMonth = getPeriodsRange(latestPeriod, 1, 1);
          return {
            period1: prevMonth,
            period2: currentMonth,
          };
        }
        case "last_3m_vs_prev": {
          const currentPeriods = getPeriodsRange(latestPeriod, 3, 0);
          const prevPeriods = getPeriodsRange(latestPeriod, 3, 3);
          return {
            period1: prevPeriods,
            period2: currentPeriods,
          };
        }
        case "last_6m_vs_prev": {
          const currentPeriods = getPeriodsRange(latestPeriod, 6, 0);
          const prevPeriods = getPeriodsRange(latestPeriod, 6, 6);
          return {
            period1: prevPeriods,
            period2: currentPeriods,
          };
        }
        case "last_12m_vs_prev": {
          const currentPeriods = getPeriodsRange(latestPeriod, 12, 0);
          const prevPeriods = getPeriodsRange(latestPeriod, 12, 12);
          return {
            period1: prevPeriods,
            period2: currentPeriods,
          };
        }
        default:
          return null;
      }
    },
    [availablePeriods, getPeriodsRange, getYTDPeriods],
  );

  // Function to format period name for display
  const formatPeriodName = useCallback(
    (periods) => {
      if (!periods || periods.length === 0) return "";

      const formatMonth = (period) => {
        const year = Math.floor(period / 100);
        const month = period % 100;
        return t("months." + month) + "-" + year.toString().slice(-2);
      };

      if (periods.length === 1) {
        return formatMonth(periods[0]);
      } else {
        // Sort periods to get oldest and newest
        const sortedPeriods = [...periods].sort((a, b) => a - b);
        const oldest = sortedPeriods[0];
        const newest = sortedPeriods[sortedPeriods.length - 1];
        return formatMonth(oldest) + " a " + formatMonth(newest);
      }
    },
    [t],
  );

  // Function to convert periods (YYYYMM) to [year, month] format expected by backend
  const periodsToBackendFormat = useCallback((periods) => {
    return periods.map((period) => {
      const year = Math.floor(period / 100);
      const month = period % 100;
      return [year, month];
    });
  }, []);

  // Function to refresh both P&Ls
  const refreshPLs = useCallback(() => {
    if (isLoading || !comparisonPeriod) return;

    const periodsData = getPeriodsForComparison(comparisonPeriod);
    if (!periodsData) return;

    setPlRefreshing(true);

    // Update P&L names based on periods BEFORE API call
    // Note: formatPeriodName is intentionally not in dependencies to avoid reloading data when language changes
    // Names are updated separately in a dedicated useEffect
    setPlNames({
      1: formatPeriodName(periodsData.period1),
      2: formatPeriodName(periodsData.period2),
    });

    // Prepare common filters (excluding period which will be P&L specific)
    const filteredCommonFilters = { ...commonFilters };
    const filteredCommonFiltersMode = { ...commonFiltersMode };
    const filteredCommonFiltersSequence = commonFiltersSequence.filter(
      (f) => f !== "period",
    );
    delete filteredCommonFilters.period;
    delete filteredCommonFiltersMode.period;

    // Refresh P&L 1 (older period)
    const rangeFiltersArray =
      Object.keys(rangeFilters).length > 0
        ? Object.entries(rangeFilters).map(([dim, f]) => ({
            dimension: dim,
            mode: f.mode,
            min: f.min,
            max: f.max,
            ref_line: f.refLine,
          }))
        : null;

    const params1 = {
      currency_unit: currencyUnit,
      volume_column: volumeColumn,
      revenue_reference: revenueReference,
      common_filters: {
        ...filteredCommonFilters,
        period: periodsToBackendFormat(periodsData.period1),
      },
      common_filters_mode: {
        ...filteredCommonFiltersMode,
        period: "items",
      },
      common_filters_sequence: ["period", ...filteredCommonFiltersSequence],
      pl_filters: {},
      pl_filters_mode: {},
      pl_filters_sequence: [],
      ref_range_filters: rangeFiltersArray,
      ref_color_line: rangeFiltersArray ? rangeFiltersArray[0]?.ref_line || null : null,
    };

    // Refresh P&L 2 (newer period)
    const params2 = {
      currency_unit: currencyUnit,
      volume_column: volumeColumn,
      revenue_reference: revenueReference,
      common_filters: {
        ...filteredCommonFilters,
        period: periodsToBackendFormat(periodsData.period2),
      },
      common_filters_mode: {
        ...filteredCommonFiltersMode,
        period: "items",
      },
      common_filters_sequence: ["period", ...filteredCommonFiltersSequence],
      pl_filters: {},
      pl_filters_mode: {},
      pl_filters_sequence: [],
      ref_range_filters: rangeFiltersArray,
      ref_color_line: rangeFiltersArray ? rangeFiltersArray[0]?.ref_line || null : null,
    };

    // Call API for both P&Ls
    Promise.all([
      client.post("api/results_pl_explorer_read_pl/", params1),
      client.post("api/results_pl_explorer_read_pl/", params2),
    ])
      .then(([response1, response2]) => {
        setPlValues({
          1: response1.data.pl,
          2: response2.data.pl,
        });

        setPlRefreshing(false);
      })
      .catch((error) => {
        console.error("Error refreshing P&Ls:", error);
        setPlRefreshing(false);
      });
  }, [
    isLoading,
    comparisonPeriod,
    getPeriodsForComparison,
    commonFilters,
    commonFiltersMode,
    commonFiltersSequence,
    rangeFilters,
    currencyUnit,
    volumeColumn,
    periodsToBackendFormat,
  ]);

  // Function to refresh drivers data
  const refreshDrivers = useCallback(() => {
    if (isLoading || !comparisonPeriod) return;

    const periodsData = getPeriodsForComparison(comparisonPeriod);
    if (!periodsData) return;

    setDriversRefreshing(true);

    // Prepare common filters (excluding period)
    const filteredCommonFilters = { ...commonFilters };
    const filteredCommonFiltersMode = { ...commonFiltersMode };
    const filteredCommonFiltersSequence = commonFiltersSequence.filter(
      (f) => f !== "period",
    );
    delete filteredCommonFilters.period;
    delete filteredCommonFiltersMode.period;

    // Prepare parameters for both periods
    const params = {
      currency_unit: currencyUnit,
      volume_column: volumeColumn,
      revenue_reference: revenueReference,
      // Period 1 (older)
      common_filters_p1: {
        ...filteredCommonFilters,
        period: periodsToBackendFormat(periodsData.period1),
      },
      common_filters_mode_p1: {
        ...filteredCommonFiltersMode,
        period: "items",
      },
      common_filters_sequence_p1: ["period", ...filteredCommonFiltersSequence],
      // Period 2 (newer)
      common_filters_p2: {
        ...filteredCommonFilters,
        period: periodsToBackendFormat(periodsData.period2),
      },
      common_filters_mode_p2: {
        ...filteredCommonFiltersMode,
        period: "items",
      },
      common_filters_sequence_p2: ["period", ...filteredCommonFiltersSequence],
    };

    client
      .post("api/results_trends_drivers/", params)
      .then((response) => {
        setDriversData(response.data);
        setDriversRefreshing(false);
      })
      .catch((error) => {
        console.error("Error refreshing drivers:", error);
        setDriversRefreshing(false);
      });
  }, [
    isLoading,
    comparisonPeriod,
    getPeriodsForComparison,
    commonFilters,
    commonFiltersMode,
    commonFiltersSequence,
    currencyUnit,
    volumeColumn,
    periodsToBackendFormat,
  ]);

  // Effect to refresh P&Ls and drivers when comparison period or filters change
  useEffect(() => {
    if (comparisonPeriod && !isLoading) {
      refreshPLs();
      refreshDrivers();
    }
  }, [
    comparisonPeriod,
    commonFilters,
    commonFiltersMode,
    commonFiltersSequence,
    rangeFilters,
    currencyUnit,
    isLoading,
    refreshPLs,
    refreshDrivers,
  ]);

  // Effect to update P&L names when language changes (without reloading data)
  useEffect(() => {
    if (!comparisonPeriod) return;

    const periodsData = getPeriodsForComparison(comparisonPeriod);
    if (!periodsData) return;

    setPlNames({
      1: formatPeriodName(periodsData.period1),
      2: formatPeriodName(periodsData.period2),
    });
  }, [
    i18n.language,
    comparisonPeriod,
    getPeriodsForComparison,
    formatPeriodName,
  ]);

  // ReactSelect styles (similar to InputsV2.js)
  const selectStyles = {
    container: (provided) => ({
      ...provided,
      width: "100%",
      flex: 1,
      marginTop: -4,
      marginBottom: -4,
    }),
    // eslint-disable-next-line no-unused-vars
    control: (provided, state) => ({
      ...provided,
      width: "100%",
      borderRadius: "0",
      borderWidth: "0",
      borderColor: "transparent",
      marginTop: "0",
      marginBottom: "0",
      boxShadow: "none",
      minHeight: "34px",
      "&:hover": {
        borderColor: "transparent",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      paddingTop: "3px",
      paddingBottom: "3px",
      backgroundColor: state.isSelected
        ? blendColors(whiteColor, clientColor, 0.6)
        : state.isFocused
          ? blendColors(whiteColor, clientColor, 0.9)
          : whiteColor,
      color: state.isDisabled
        ? grayColor[10] // Light gray for disabled options
        : state.isSelected
          ? whiteColor
          : blackColor,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      "&:active": {
        backgroundColor: state.isDisabled
          ? whiteColor
          : blendColors(whiteColor, clientColor, 0.75),
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "2px 8px",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: blackColor,
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: "2px",
      marginBottom: "0",
      zIndex: "9999",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: "4px",
      color: state.isFocused ? clientColor : grayColor[7],
      "&:hover": { color: clientColor },
    }),
  };

  // Registra o callback de P&L meta — executado pelo hook useResultsFilters
  // após carregar api/results_trends_meta/ (filtros já inicializados pelo hook)
  trendsMetaCallback.current = (response) => {
    // Extrair períodos disponíveis para o seletor de comparação
    const rawPeriods = response.data.filters.items.period || [];
    const convertedPeriods = rawPeriods.map((period) => {
      if (Array.isArray(period)) {
        return period[0] * 100 + period[1];
      }
      return period;
    });
    setAvailablePeriods(convertedPeriods);

    const pl = response.data.pl;
    volumeUnit.current = pl.volume_unit;
    volumeUnitAlternatives.current = pl.volume_unit_alternatives || [];
    currencyUnitOptions.current = pl.currency_unit_options;
    currencyUnitOptionsAvailable.current = pl.currency_unit_options_available;
    setCurrencyUnit(pl.default_currency_unit);
    plStructure.current = pl.pl_structure;
    revenueReferenceOptions.current = pl.revenue_reference_options || [];
    grossRevenueAnchor.current =
      revenueReferenceOptions.current.length > 0
        ? revenueReferenceOptions.current[0].value
        : null;
    if (revenueReference === null && revenueReferenceOptions.current.length > 0) {
      const defaultOpt = revenueReferenceOptions.current.find((o) => o.default);
      setRevenueReference(
        defaultOpt ? defaultOpt.value : pl.net_revenue_anchor,
      );
    }
    netRevenueAnchor.current = pl.net_revenue_anchor;
    serveMarginAnchor.current = pl.serve_margin_anchor;
    revenueAnchors.current = pl.revenue_anchors;
    marginAnchors.current = pl.margin_anchors;
    volumeL12M.current = pl.volume_l12m;

    setPlValues({ 1: {}, 2: {} });
    setIsLoading(false);
  };

  useEffect(() => {
    // Initialize drill downs after plStructure is loaded
    if (plStructure.current && plStructure.current.length > 0) {
      let new_drill_downs_open = [];
      plStructure.current.forEach((plLine) => {
        if (plLine.is_parent) new_drill_downs_open.push(plLine.meta_name);
      });
      setDrillDownsOpen(new_drill_downs_open);
    }
  }, [isLoading]);

  useLayoutEffect(() => {
    // changes the order of magnitude in which all the P&Ls are presented
    //    - fired when the plValues or language is modified
    //    - store results in 'oom' object

    const calculatedOom = calculateOomFromPlValues(plValues, t);
    setOom(calculatedOom);
  }, [plValues, i18n.language, t]);

  useLayoutEffect(() => {
    // Calculate volume-specific order of magnitude based on delta
    if (!plValues[1]?.volume || !plValues[2]?.volume) {
      setVolumeOom({ div: 1, x: "" });
      return;
    }

    const volumeDelta = Math.abs(plValues[2].volume - plValues[1].volume);

    if (volumeDelta === 0) {
      setVolumeOom({ div: 1, x: "" });
      return;
    }

    const calculatedVolumeOom = getOom(volumeDelta);
    const x =
      calculatedVolumeOom.x === ""
        ? ""
        : " " + (t ? t(`oom.${calculatedVolumeOom.x}`) : calculatedVolumeOom.x);

    setVolumeOom({
      div: calculatedVolumeOom.div,
      x: x,
    });
  }, [plValues, i18n.language, t]);

  const graphOptions = (
    <>
      {/* Unit */}
      <ActionDropdown
        button={
          <GridItem className={classes.actionWrapperOuter}>
            <div
              className={cx({
                [classes.actionWrapper]: true,
                [classes.actionDisabled]: isLoading,
              })}
            >
              <div className={classes.actionIcon}>
                <AttachMoney />
              </div>
              <h6 className={classes.actionText}>{t("results.unit")}</h6>
            </div>
          </GridItem>
        }
        dropdownList={getUnitOptions()}
        onClick={handleUnitSelection}
      />
      {/* Take photo */}
      <GridItem className={classes.actionWrapperOuter}>
        <div
          className={cx({
            [classes.actionWrapper]: true,
            [classes.actionDisabled]: isLoading || levelsCount.current === 0,
          })}
          onClick={levelsCount.current === 0 ? undefined : downloadScreenshot}
          style={{
            cursor:
              isLoading || levelsCount.current === 0 ? "default" : "pointer",
          }}
        >
          <div className={classes.actionIcon}>
            <PhotoCamera />
          </div>
          <h6 className={classes.actionText}>{t("results.take_photo")}</h6>
        </div>
      </GridItem>
      {/* Help */}
      <GridItem className={classes.actionWrapperOuter}>
        <div
          className={cx({
            [classes.actionWrapper]: true,
            [classes.actionDisabled]: isLoading,
          })}
          onClick={handleHelpClick}
          style={{ cursor: isLoading ? "default" : "pointer" }}
        >
          <div className={classes.actionIcon}>
            <HelpOutline />
          </div>
          <h6 className={classes.actionText}>{t("results.help")}</h6>
        </div>
      </GridItem>
    </>
  );

  return (
    <>
      <div>
        <Hidden smDown>
          <ActionBar actions={graphOptions} spinnerIn={false} />
        </Hidden>
        <div ref={trendsRef}>
          <GridContainer className={classes.gridContainerTopNegative} alignItems="stretch">
            <Hidden mdUp>
              <GridItem xs={12}>
                <ActionBar actions={graphOptions} spinnerIn={false} sm />
              </GridItem>
            </Hidden>

            {/* ── Barra de filtros (metade esquerda) ──────────────────────── */}
            <ResultsFilterBar
              gridItemProps={{ md: 6 }}
              filtersList={filtersList}
              filtersNames={filtersNames}
              filtersGroups={filtersGroups}
              commonFilters={commonFilters}
              commonFiltersMode={commonFiltersMode}
              commonFiltersSequence={commonFiltersSequence}
              filterMenuClickHandler={filterMenuClickHandler}
              removeFilter={removeFilter}
              dimFilterDialogProps={dimFilterDialogProps}
              clientColor={clientColor}
              sections={dimSections}
              rangeFilters={rangeFilters}
              clearRangeFilter={clearRangeFilter}
              showPeriodExtras={true}
              periodExtrasProps={{
                hidePeriod: true,
                onReset: handleResetFilters,
                persist,
                onPersistChange: setPersist,
                availablePeriods: [],
                selectedPeriodKeys: null,
                activePreset: null,
                onPeriodApply: () => {},
              }}
            />

            {/* ── Seletor de período de comparação (metade direita) ─────── */}
            <GridItem xs={12} md={6}>
              <Card style={{ marginTop: 18, marginBottom: 8 }}>
                <CardBody
                  style={{
                    paddingTop: 4,
                    paddingBottom: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: "#888",
                      fontWeight: 400,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("results.trends.comparison_period")}:
                  </span>
                  <div className={classes.comparisonPeriodWrapper}>
                    <ReactSelect
                      isDisabled={isLoading || plRefreshing}
                      value={comparisonPeriodOptions.find(
                        (opt) => opt.value === comparisonPeriod,
                      )}
                      onChange={handleComparisonPeriodChange}
                      options={comparisonPeriodOptions}
                      isOptionDisabled={(option) => option.isDisabled}
                      styles={selectStyles}
                      placeholder={t("results.trends.comparison_period")}
                    />
                  </div>
                </CardBody>
              </Card>
            </GridItem>
            {!isLoading && (
              <>
                {/* Change Indicators Card - top of page */}
                <GridItem
                  xs={12}
                  className={cx(
                    classes.indicatorsCard,
                  )}
                >
                  <Card className={classes.cardNoMargin}>
                    <CardBody className={classes.indicatorsCardBody}>
                      <div className={classes.indicatorsGrid}>
                        {/* Volume */}
                        <div className={classes.indicatorWrapper}>
                          <div className={classes.indicatorLabel}>Δ Volume</div>
                          {plRefreshing || !changeIndicators ? (
                            <div className={classes.indicatorLoadingWrapper}>
                              <ThreeDots
                                width={"36px"}
                                color={clientColor}
                                ariaLabel="loading"
                              />
                            </div>
                          ) : (
                            <>
                              <div
                                className={cx(
                                  classes.indicatorValue,
                                  changeIndicators.volume.deltaPercent < 0 &&
                                    classes.indicatorValueNegative,
                                )}
                              >
                                {changeIndicators.volume.deltaPercent >= 0
                                  ? "+"
                                  : ""}
                                {changeIndicators.volume.deltaPercent.toFixed(
                                  1,
                                )}
                                %
                              </div>
                              <div
                                className={cx(
                                  classes.indicatorPercent,
                                  changeIndicators.volume.delta < 0 &&
                                    classes.indicatorPercentNegative,
                                )}
                              >
                                {changeIndicators.volume.delta >= 0 ? "+" : ""}
                                {Math.round(
                                  changeIndicators.volume.delta / volumeOom.div,
                                )}
                                {volumeOom.x} {volumeUnit.current}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Gross Price */}
                        <div className={classes.indicatorWrapper}>
                          <div className={classes.indicatorLabel}>
                            Δ {t("results.trends.gross_price")}{" "}
                            <HelpTooltip
                              small
                              tooltip={`${plStructure.current.find((line) => line.meta_name === grossRevenueAnchor.current)?.name || "Faturamento bruto"} / ${t("results.volume")}`}
                            />
                          </div>
                          {plRefreshing || !changeIndicators ? (
                            <div className={classes.indicatorLoadingWrapper}>
                              <ThreeDots
                                width={"36px"}
                                color={clientColor}
                                ariaLabel="loading"
                              />
                            </div>
                          ) : (
                            <>
                              <div
                                className={cx(
                                  classes.indicatorValue,
                                  changeIndicators.grossPrice.deltaPercent <
                                    0 && classes.indicatorValueNegative,
                                )}
                              >
                                {changeIndicators.grossPrice.deltaPercent >= 0
                                  ? "+"
                                  : ""}
                                {changeIndicators.grossPrice.deltaPercent.toFixed(
                                  1,
                                )}
                                %
                              </div>
                              <div
                                className={cx(
                                  classes.indicatorPercent,
                                  changeIndicators.grossPrice.delta < 0 &&
                                    classes.indicatorPercentNegative,
                                )}
                              >
                                {changeIndicators.grossPrice.delta >= 0
                                  ? "+"
                                  : ""}
                                {changeIndicators.grossPrice.delta.toFixed(2)}{" "}
                                {currencyUnit}/{volumeUnit.current}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Net Revenue */}
                        <div className={classes.indicatorWrapper}>
                          <div className={classes.indicatorLabel}>
                            Δ{" "}
                            {plStructure.current.find(
                              (line) =>
                                line.meta_name === netRevenueAnchor.current,
                            )?.name || "Receita Líquida"}
                          </div>
                          {plRefreshing || !changeIndicators ? (
                            <div className={classes.indicatorLoadingWrapper}>
                              <ThreeDots
                                width={"36px"}
                                color={clientColor}
                                ariaLabel="loading"
                              />
                            </div>
                          ) : (
                            <>
                              <div
                                className={cx(
                                  classes.indicatorValue,
                                  changeIndicators.netRevenue.deltaPercent <
                                    0 && classes.indicatorValueNegative,
                                )}
                              >
                                {changeIndicators.netRevenue.deltaPercent >= 0
                                  ? "+"
                                  : ""}
                                {changeIndicators.netRevenue.deltaPercent.toFixed(
                                  1,
                                )}
                                %
                              </div>
                              <div
                                className={cx(
                                  classes.indicatorPercent,
                                  changeIndicators.netRevenue.delta < 0 &&
                                    classes.indicatorPercentNegative,
                                )}
                              >
                                {changeIndicators.netRevenue.delta >= 0
                                  ? "+"
                                  : ""}
                                {Math.round(
                                  changeIndicators.netRevenue.delta / oom.div,
                                )}{" "}
                                {currencyUnit}
                                {oom.x}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Serve Margin */}
                        <div className={classes.indicatorWrapper}>
                          <div className={classes.indicatorLabel}>
                            Δ{" "}
                            {plStructure.current.find(
                              (line) =>
                                line.meta_name === serveMarginAnchor.current,
                            )?.name || "Margem de Servir"}
                          </div>
                          {plRefreshing || !changeIndicators ? (
                            <div className={classes.indicatorLoadingWrapper}>
                              <ThreeDots
                                width={"36px"}
                                color={clientColor}
                                ariaLabel="loading"
                              />
                            </div>
                          ) : (
                            <>
                              <div
                                className={cx(
                                  classes.indicatorValue,
                                  changeIndicators.serveMargin
                                    .deltaPercentagePoints < 0 &&
                                    classes.indicatorValueNegative,
                                )}
                              >
                                {changeIndicators.serveMargin
                                  .deltaPercentagePoints >= 0
                                  ? "+"
                                  : ""}
                                {changeIndicators.serveMargin.deltaPercentagePoints.toFixed(
                                  1,
                                )}{" "}
                                pp
                              </div>
                              <div
                                className={cx(
                                  classes.indicatorPercent,
                                  changeIndicators.serveMargin.delta < 0 &&
                                    classes.indicatorPercentNegative,
                                )}
                              >
                                {changeIndicators.serveMargin.delta >= 0
                                  ? "+"
                                  : ""}
                                {Math.round(
                                  changeIndicators.serveMargin.delta / oom.div,
                                )}{" "}
                                {currencyUnit}
                                {oom.x}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </GridItem>

                <GridContainer className={classes.plGridContainer}>
                  {/* Left column: P&Ls (50% total) */}
                  <GridItem xs={12} md={6}>
                    <GridContainer>
                      <PnlSharedProvider
                        plValues={plValues}
                        plStructure={plStructure}
                        benchmark={null}
                        netRevenueAnchor={netRevenueAnchor}
                        serveMarginAnchor={serveMarginAnchor}
                        oom={oom}
                        t={t}
                      >
                        {/* P&L lines */}
                        <GridItem
                          xs={12}
                          md={4}
                          className={classes.plCardWrapper}
                        >
                          <Card
                            className={classes.plCard}
                            onMouseEnter={allowTimers}
                            onMouseLeave={disallowTimers}
                          >
                            <CardBody className={classes.plCardBody}>
                              <Pnl
                                plStructure={plStructure}
                                revenueAnchors={revenueAnchors.current}
                                marginAnchors={marginAnchors.current}
                                netRevenueAnchor={netRevenueAnchor}
                                serveMarginAnchor={serveMarginAnchor}
                                numPL={1}
                                numPLs={2}
                                plName={"-"}
                                handleMouseLeavePLLine={handleMouseLeavePLLine}
                                handleMouseOverPLLine={handleMouseOverPLLine}
                                mouseOverPLLine={mouseOverPLLine}
                                mouseOverPLLineFade={mouseOverPLLineFade}
                                timersAllowed={timersAllowed.current}
                                drillDownsOpen={drillDownsOpen}
                                setDrillDownsOpen={setDrillDownsOpen}
                                plValues={plValues}
                                volumeUnit={volumeUnit}
                                volumeL12M={{ current: null }}
                                currencyUnit={currencyUnit}
                                oom={oom}
                                isLoading={isLoading}
                                isRefreshing={plRefreshing}
                                plsToRefresh={[]}
                                showColumns={["name"]}
                                fixedHighlightLine={serveMarginAnchor.current}
                              />
                            </CardBody>
                          </Card>
                        </GridItem>
                        {/* First P&L */}
                        <GridItem
                          xs={12}
                          md={3}
                          className={classes.plCardWrapper}
                        >
                          <Card
                            className={classes.plCard}
                            onMouseEnter={allowTimers}
                            onMouseLeave={disallowTimers}
                          >
                            <CardBody className={classes.plCardBody}>
                              <Pnl
                                plStructure={plStructure}
                                revenueAnchors={revenueAnchors.current}
                                marginAnchors={marginAnchors.current}
                                netRevenueAnchor={netRevenueAnchor}
                                serveMarginAnchor={serveMarginAnchor}
                                numPL={1}
                                numPLs={2}
                                plName={plNames[1] || "-"}
                                handleMouseLeavePLLine={handleMouseLeavePLLine}
                                handleMouseOverPLLine={handleMouseOverPLLine}
                                mouseOverPLLine={mouseOverPLLine}
                                mouseOverPLLineFade={mouseOverPLLineFade}
                                timersAllowed={timersAllowed.current}
                                drillDownsOpen={drillDownsOpen}
                                setDrillDownsOpen={setDrillDownsOpen}
                                plValues={plValues}
                                volumeUnit={volumeUnit}
                                volumeL12M={{ current: null }}
                                currencyUnit={currencyUnit}
                                oom={oom}
                                isLoading={isLoading}
                                isRefreshing={plRefreshing}
                                plsToRefresh={plRefreshing ? [1] : []}
                                showColumns={["val", "percentage"]}
                                fixedHighlightLine={serveMarginAnchor.current}
                              />
                            </CardBody>
                          </Card>
                        </GridItem>
                        {/* Second P&L */}
                        <GridItem
                          xs={12}
                          md={3}
                          className={classes.plCardWrapper}
                        >
                          <Card
                            className={classes.plCard}
                            onMouseEnter={allowTimers}
                            onMouseLeave={disallowTimers}
                          >
                            <CardBody className={classes.plCardBody}>
                              <Pnl
                                plStructure={plStructure}
                                revenueAnchors={revenueAnchors.current}
                                marginAnchors={marginAnchors.current}
                                netRevenueAnchor={netRevenueAnchor}
                                serveMarginAnchor={serveMarginAnchor}
                                numPL={2}
                                numPLs={2}
                                plName={plNames[2] || "-"}
                                handleMouseLeavePLLine={handleMouseLeavePLLine}
                                handleMouseOverPLLine={handleMouseOverPLLine}
                                mouseOverPLLine={mouseOverPLLine}
                                mouseOverPLLineFade={mouseOverPLLineFade}
                                timersAllowed={timersAllowed.current}
                                drillDownsOpen={drillDownsOpen}
                                setDrillDownsOpen={setDrillDownsOpen}
                                plValues={plValues}
                                volumeUnit={volumeUnit}
                                volumeL12M={{ current: null }}
                                currencyUnit={currencyUnit}
                                oom={oom}
                                isLoading={isLoading}
                                isRefreshing={plRefreshing}
                                plsToRefresh={plRefreshing ? [2] : []}
                                showColumns={["val", "percentage"]}
                                fixedHighlightLine={serveMarginAnchor.current}
                              />
                            </CardBody>
                          </Card>
                        </GridItem>
                        {/* P&L deltas */}
                        <GridItem
                          xs={12}
                          md={2}
                          className={classes.plCardWrapper}
                        >
                          <Card
                            className={classes.plCard}
                            onMouseEnter={allowTimers}
                            onMouseLeave={disallowTimers}
                          >
                            <CardBody className={classes.plCardBody}>
                              <Pnl
                                plStructure={plStructure}
                                revenueAnchors={revenueAnchors.current}
                                marginAnchors={marginAnchors.current}
                                netRevenueAnchor={netRevenueAnchor}
                                serveMarginAnchor={serveMarginAnchor}
                                numPL={2}
                                numPLs={2}
                                plName={"Δ"}
                                handleMouseLeavePLLine={handleMouseLeavePLLine}
                                handleMouseOverPLLine={handleMouseOverPLLine}
                                mouseOverPLLine={mouseOverPLLine}
                                mouseOverPLLineFade={mouseOverPLLineFade}
                                timersAllowed={timersAllowed.current}
                                drillDownsOpen={drillDownsOpen}
                                setDrillDownsOpen={setDrillDownsOpen}
                                plValues={plValues}
                                volumeUnit={volumeUnit}
                                volumeL12M={{ current: null }}
                                currencyUnit={currencyUnit}
                                oom={oom}
                                isLoading={isLoading}
                                isRefreshing={plRefreshing}
                                plsToRefresh={plRefreshing ? [2] : []}
                                showColumns={["delta"]}
                                fixedHighlightLine={serveMarginAnchor.current}
                              />
                            </CardBody>
                          </Card>
                        </GridItem>
                      </PnlSharedProvider>
                    </GridContainer>
                  </GridItem>

                  {/* Right column: Chart and Tabs (50% total) */}
                  <GridItem xs={12} md={6}>
                    <GridContainer>
                      {/* Building Block Chart Card - top right */}
                      <GridItem xs={12} style={{ marginTop: "8px" }}>
                        <Card className={classes.cardWaterfallMargin}>
                          <CardBody className={classes.buildingBlockCardBody}>
                            <div className={classes.buildingBlockTitleWrapper}>
                              <div className={classes.buildingBlockTitle}>
                                {t("results.trends.waterfall_title")}
                              </div>
                              <HelpTooltip
                                tooltip={
                                  <div
                                    className={classes.waterfallTooltipContent}
                                  >
                                    <div
                                      className={
                                        classes.waterfallTooltipSection
                                      }
                                    >
                                      <strong>
                                        {t("results.trends.price_delta")}
                                      </strong>
                                      <div
                                        className={classes.waterfallTooltipText}
                                      >
                                        {t(
                                          "results.trends.price_delta_tooltip1",
                                        )}
                                      </div>
                                      <div
                                        className={
                                          classes.waterfallTooltipItalic
                                        }
                                      >
                                        {t(
                                          "results.trends.price_delta_tooltip2",
                                        )}
                                      </div>
                                    </div>
                                    <div
                                      className={
                                        classes.waterfallTooltipSection
                                      }
                                    >
                                      <strong>
                                        {t("results.trends.cost_delta")}
                                      </strong>
                                      <div
                                        className={classes.waterfallTooltipText}
                                      >
                                        {t(
                                          "results.trends.cost_delta_tooltip1",
                                        )}
                                      </div>
                                      <div
                                        className={
                                          classes.waterfallTooltipItalic
                                        }
                                      >
                                        {t(
                                          "results.trends.cost_delta_tooltip2",
                                        )}
                                      </div>
                                    </div>
                                    <div
                                      className={
                                        classes.waterfallTooltipSectionLast
                                      }
                                    >
                                      <strong>
                                        {t("results.trends.mix_delta")}
                                      </strong>
                                      <div
                                        className={classes.waterfallTooltipText}
                                      >
                                        {t("results.trends.mix_delta_tooltip1")}
                                      </div>
                                      <div
                                        className={
                                          classes.waterfallTooltipItalic
                                        }
                                      >
                                        {t("results.trends.mix_delta_tooltip2")}
                                      </div>
                                    </div>
                                  </div>
                                }
                                tooltipProps={{
                                  PopperProps: {
                                    modifiers: {
                                      offset: {
                                        enabled: true,
                                        offset: "0, 10",
                                      },
                                    },
                                  },
                                  classes: {
                                    tooltip: classes.waterfallTooltip,
                                  },
                                }}
                              />
                            </div>
                            <div className={classes.waterfallChartWrapper}>
                              {!isLoading && plRefreshing && (
                                <div
                                  className={
                                    classes.waterfallChartLoadingWrapper
                                  }
                                >
                                  <ThreeDots
                                    color={clientColor}
                                    height={50}
                                    width={50}
                                  />
                                </div>
                              )}
                              {!isLoading && !plRefreshing && waterfallData && (
                                <Bar
                                  data={{
                                    labels: [
                                      t("results.trends.initial_margin"),
                                      t("results.trends.price_delta"),
                                      t("results.trends.cost_delta"),
                                      t("results.trends.mix_delta"),
                                      t("results.trends.final_margin"),
                                    ],
                                    datasets: [
                                      {
                                        label: "Values",
                                        data: [
                                          [0, waterfallData.marginInitial],
                                          [
                                            waterfallData.marginInitial,
                                            waterfallData.marginInitial +
                                              waterfallData.priceEffect,
                                          ],
                                          [
                                            waterfallData.marginInitial +
                                              waterfallData.priceEffect,
                                            waterfallData.marginInitial +
                                              waterfallData.priceEffect +
                                              waterfallData.costEffect,
                                          ],
                                          [
                                            waterfallData.marginInitial +
                                              waterfallData.priceEffect +
                                              waterfallData.costEffect,
                                            waterfallData.marginInitial +
                                              waterfallData.priceEffect +
                                              waterfallData.costEffect +
                                              waterfallData.mixEffect,
                                          ],
                                          [0, waterfallData.marginFinal],
                                        ],
                                        backgroundColor: function (context) {
                                          const index = context.dataIndex;
                                          if (index === 0 || index === 4) {
                                            return clientColor;
                                          }
                                          const value = context.raw;
                                          const delta = value[1] - value[0];
                                          return delta >= 0
                                            ? "#66BB6A"
                                            : "#EF5350";
                                        },
                                        borderWidth: 0,
                                        borderRadius: 6,
                                        borderSkipped: function (context) {
                                          const value = context.raw;
                                          const delta = value[1] - value[0];
                                          if (delta >= 0) {
                                            return "bottom";
                                          } else {
                                            return "top";
                                          }
                                        },
                                        datalabels: {
                                          display: true,
                                          color: function (context) {
                                            return context.dataIndex === 0 ||
                                              context.dataIndex === 4
                                              ? "#ffffff"
                                              : "#333";
                                          },
                                          anchor: "center",
                                          align: "center",
                                          font: {
                                            family:
                                              '"Roboto", "Helvetica", "Arial", sans-serif',
                                            weight: "300",
                                            size: 18,
                                          },
                                          formatter: function (value, context) {
                                            const delta = value[1] - value[0];
                                            if (
                                              context.dataIndex === 0 ||
                                              context.dataIndex === 4
                                            ) {
                                              return (
                                                value[1].toFixed(1) + " p.p."
                                              );
                                            }
                                            return (
                                              (delta >= 0 ? "+" : "") +
                                              delta.toFixed(1) +
                                              " p.p."
                                            );
                                          },
                                        },
                                      },
                                    ],
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      legend: {
                                        display: false,
                                      },
                                      tooltip: {
                                        enabled: false,
                                      },
                                    },
                                    scales: {
                                      x: {
                                        border: {
                                          display: false,
                                        },
                                        grid: {
                                          display: false,
                                        },
                                        ticks: {
                                          font: {
                                            family:
                                              '"Roboto", "Helvetica", "Arial", sans-serif',
                                            weight: "300",
                                            size: 14,
                                          },
                                        },
                                      },
                                      y: {
                                        beginAtZero: true,
                                        border: {
                                          display: false,
                                        },
                                        ticks: {
                                          display: false,
                                          font: {
                                            family:
                                              '"Roboto", "Helvetica", "Arial", sans-serif',
                                            weight: "300",
                                          },
                                        },
                                        grid: {
                                          display: true,
                                          drawBorder: false,
                                          drawTicks: false,
                                          color: function (context) {
                                            if (context.tick.value === 0) {
                                              return "#999";
                                            }
                                            return "transparent";
                                          },
                                          lineWidth: 1,
                                        },
                                      },
                                    },
                                  }}
                                />
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </GridItem>

                      <GridItem
                        xs={12}
                        className={cx(
                          classes.indicatorsCard,
                        )}
                      >
                        <Card className={classes.cardNoMargin}>
                          <CardBody className={classes.driversCardBody}>
                            {driversRefreshing ? (
                              <div className={classes.driversRefreshingWrapper}>
                                <ThreeDots
                                  color={clientColor}
                                  height={50}
                                  width={50}
                                />
                              </div>
                            ) : driversData ? (
                              <div className={classes.driverTreeContainer}>
                                {(() => {
                                  // ====================================================================
                                  // DRIVER RENDERING (Simplified)
                                  // ====================================================================
                                  // - Clicking on element name adds filter to page
                                  // ====================================================================

                                  const renderDriver = (driver, idx) => {
                                    const isNegative = driver.total_impact < 0;
                                    const relevanceArrow = getRelevanceArrow(
                                      driver.total_impact,
                                    );
                                    const narrative = buildNarrative(driver);
                                    const handleElementClick = () => {
                                      // Add filter for this element's dimension
                                      if (!driver.dimension || !driver.element)
                                        return;

                                      const dimKey =
                                        typeof driver.dimension === "string"
                                          ? driver.dimension
                                          : driver.dimension.en;
                                      const elementValue = driver.element;

                                      // Check if dimension exists in filtersList
                                      if (
                                        !filtersList.current?.includes(dimKey)
                                      ) {
                                        console.warn(
                                          "Dimension not found in filtersList:",
                                          dimKey,
                                        );
                                        return;
                                      }

                                      // Find element index
                                      const elementIndex =
                                        filtersItems.current[dimKey]?.indexOf(
                                          elementValue,
                                        );
                                      if (
                                        elementIndex === undefined ||
                                        elementIndex === -1
                                      ) {
                                        console.warn(
                                          `Element "${elementValue}" not found in dimension "${dimKey}"`,
                                        );
                                        return;
                                      }

                                      // Update filters
                                      const newFilterValues = commonFilters[
                                        dimKey
                                      ]
                                        ? [...commonFilters[dimKey]]
                                        : [];
                                      if (
                                        !newFilterValues.includes(elementValue)
                                      ) {
                                        newFilterValues.push(elementValue);
                                      }

                                      const newFilterIndexes =
                                        commonFiltersIndexes[dimKey]
                                          ? [...commonFiltersIndexes[dimKey]]
                                          : [];
                                      if (
                                        !newFilterIndexes.includes(elementIndex)
                                      ) {
                                        newFilterIndexes.push(elementIndex);
                                      }

                                      // Apply updates
                                      setCommonFilters((draft) => {
                                        draft[dimKey] = newFilterValues;
                                      });

                                      setCommonFiltersIndexes((draft) => {
                                        draft[dimKey] = newFilterIndexes;
                                      });

                                      setCommonFiltersMode((draft) => {
                                        draft[dimKey] = "items";
                                      });

                                      if (
                                        !commonFiltersSequence.includes(dimKey)
                                      ) {
                                        setCommonFiltersSequence([
                                          ...commonFiltersSequence,
                                          dimKey,
                                        ]);
                                      }
                                    };

                                    return (
                                      <div
                                        key={idx}
                                        className={classes.driverRowWrapperRoot}
                                        style={{ marginBottom: "3px" }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "6px 10px",
                                            backgroundColor: "#fff",
                                            borderLeft: `3px solid ${isNegative ? "#d32f2f" : "#388e3c"}`,
                                            borderRadius: "0 4px 4px 0",
                                            transition: "all 0.2s",
                                          }}
                                        >
                                          {/* Relevance Arrow */}
                                          <Tooltip
                                            title={
                                              relevanceArrow.relevanceLevel ===
                                              "high"
                                                ? t(
                                                    "results.trends.relevance.high_tooltip",
                                                  )
                                                : relevanceArrow.relevanceLevel ===
                                                    "medium"
                                                  ? t(
                                                      "results.trends.relevance.medium_tooltip",
                                                    )
                                                  : t(
                                                      "results.trends.relevance.low_tooltip",
                                                    )
                                            }
                                            placement="top"
                                          >
                                            <div
                                              style={{
                                                marginRight: "8px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: "20px",
                                                height: "20px",
                                              }}
                                            >
                                              <FontAwesomeIcon
                                                icon={faArrowRight}
                                                style={{
                                                  transform: `rotate(${relevanceArrow.angle}deg)`,
                                                  color: relevanceArrow.color,
                                                  fontSize: "14px",
                                                  transition: "transform 0.3s",
                                                }}
                                              />
                                            </div>
                                          </Tooltip>

                                          {/* Inconsistency Tag */}
                                          {driver.has_inconsistency && (
                                            <Tooltip
                                              title={t(
                                                "results.trends.inconsistency_tooltip",
                                              )}
                                              placement="top"
                                            >
                                              <div
                                                style={{
                                                  padding: "2px 6px",
                                                  backgroundColor:
                                                    getInconsistencyTag()
                                                      .bgColor,
                                                  color:
                                                    getInconsistencyTag().color,
                                                  fontSize: "10px",
                                                  fontWeight: "600",
                                                  textTransform: "uppercase",
                                                  borderRadius: "3px",
                                                  letterSpacing: "0.3px",
                                                  textAlign: "center",
                                                  marginRight: "8px",
                                                  flexShrink: 0,
                                                }}
                                              >
                                                {getInconsistencyTag().label}
                                              </div>
                                            </Tooltip>
                                          )}

                                          {/* Narrative */}
                                          <div
                                            style={{
                                              flex: 1,
                                              fontSize: "13px",
                                              color: "#333",
                                              lineHeight: "1.4",
                                            }}
                                          >
                                            {narrative.beforeElement}
                                            <Tooltip
                                              title={t(
                                                "results.trends.click_to_filter",
                                              )}
                                              placement="top"
                                            >
                                              <span
                                                onClick={handleElementClick}
                                                style={{
                                                  cursor: "pointer",
                                                  textDecoration: "underline",
                                                  textDecorationStyle: "dotted",
                                                  textUnderlineOffset: "3px",
                                                  fontWeight: "600",
                                                  color: isNegative
                                                    ? "#c62828"
                                                    : "#2e7d32",
                                                }}
                                              >
                                                {narrative.element}
                                              </span>
                                            </Tooltip>
                                            <span
                                              dangerouslySetInnerHTML={{
                                                __html: narrative.afterElement,
                                              }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  };

                                  // Backend sends losses and gains already ordered by abs(total_impact)
                                  const losses = driversData.main_losses || [];
                                  const gains = driversData.main_gains || [];

                                  return (
                                    <div>
                                      {/* Losses Section */}
                                      {losses.length > 0 && (
                                        <div>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              paddingBottom: "12px",
                                              borderBottom: "1px solid #eee",
                                              marginBottom: "12px",
                                            }}
                                          >
                                            <TrendingDown
                                              style={{
                                                fontSize: 24,
                                                color: "#d32f2f",
                                                marginRight: "8px",
                                              }}
                                            />
                                            <h3
                                              style={{
                                                fontSize: "16px",
                                                fontWeight: "500",
                                                color: "#d32f2f",
                                                margin: 0,
                                              }}
                                            >
                                              {t("results.trends.main_losses")}
                                            </h3>
                                            <HelpTooltip
                                              small
                                              tooltip={
                                                <div
                                                  className={
                                                    classes.waterfallTooltipContent
                                                  }
                                                  style={{
                                                    whiteSpace: "pre-line",
                                                  }}
                                                >
                                                  {t(
                                                    "results.trends.drivers_explanation",
                                                  )}
                                                </div>
                                              }
                                              tooltipProps={{
                                                classes: {
                                                  tooltip:
                                                    classes.waterfallTooltip,
                                                },
                                              }}
                                              style={{ marginLeft: "8px" }}
                                            />
                                          </div>
                                          {losses.map((driver, idx) =>
                                            renderDriver(driver, idx),
                                          )}
                                        </div>
                                      )}

                                      {/* Gains Section */}
                                      {gains.length > 0 && (
                                        <div
                                          style={{
                                            marginTop:
                                              losses.length > 0 ? "24px" : "0",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              paddingBottom: "12px",
                                              borderBottom: "1px solid #eee",
                                              marginBottom: "12px",
                                            }}
                                          >
                                            <TrendingUp
                                              style={{
                                                fontSize: 24,
                                                color: "#388e3c",
                                                marginRight: "8px",
                                              }}
                                            />
                                            <h3
                                              style={{
                                                fontSize: "16px",
                                                fontWeight: "500",
                                                color: "#388e3c",
                                                margin: 0,
                                              }}
                                            >
                                              {t("results.trends.main_gains")}
                                            </h3>
                                          </div>
                                          {gains.map((driver, idx) =>
                                            renderDriver(driver, idx),
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div
                                style={{
                                  textAlign: "center",
                                  color: "#999",
                                  padding: "40px",
                                }}
                              >
                                {t("results.no_data_available")}
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </GridItem>
                    </GridContainer>
                  </GridItem>
                </GridContainer>
              </>
            )}
          </GridContainer>
        </div>
      </div>
      <FullPageLoader isVisible={isLoading} />
      <HelpDialog
        open={helpDialogOpen}
        setOpen={setHelpDialogOpen}
        content={[
          {
            icon: "Purpose",
            title: t("help.graph_explorer.purpose.title"),
            items: [
              {
                title: t("help.graph_explorer.purpose.objective.title"),
                description: t(
                  "help.graph_explorer.purpose.objective.description",
                ),
              },
            ],
          },
          {
            icon: "BasicFunctionality",
            title: t("help.graph_explorer.basic_functionality.title"),
            items: [
              {
                title: t("help.graph_explorer.basic_functionality.step1.title"),
                description: t(
                  "help.graph_explorer.basic_functionality.step1.description",
                ),
              },
              {
                title: t("help.graph_explorer.basic_functionality.step2.title"),
                description: t(
                  "help.graph_explorer.basic_functionality.step2.description",
                ),
              },
              {
                title: t("help.graph_explorer.basic_functionality.step3.title"),
                description: t(
                  "help.graph_explorer.basic_functionality.step3.description",
                ),
              },
            ],
          },
          {
            icon: "OtherFeatures",
            title: t("help.graph_explorer.other_features.title"),
            items: [
              {
                title: t("help.graph_explorer.other_features.currency.title"),
                description: t(
                  "help.graph_explorer.other_features.currency.description",
                ),
              },
              {
                title: t("help.graph_explorer.other_features.screenshot.title"),
                description: t(
                  "help.graph_explorer.other_features.screenshot.description",
                ),
              },
            ],
          },
        ]}
      />
    </>
  );
}

Trends.propTypes = {};
